import { lazy, Suspense, useState, type CSSProperties } from "react";
import {
  downloadArtifact,
  type FinalReport,
  type IntegrityEvent,
  type IntegrityEventType,
  type TranscriptEntryRead,
} from "@/api/client";
import { formatDate } from "@/lib/dashboard-utils";
import { NumberTicker } from "@/components/motion/number-ticker";
import { BorderBeam } from "border-beam";
import { useReducedMotion } from "motion/react";
import "@/styles/aura-report.css";

// recharts (and its d3 deps) is the heaviest thing in the bundle; the radar
// is the report's only recharts consumer, so it loads as its own chunk while
// the dossier's text renders. The fallback holds the .report-radar box so
// the margin column doesn't reflow when the chart pops in.
const SectionGradesRadar = lazy(() =>
  import("./section-grades-radar").then(m => ({ default: m.SectionGradesRadar })),
);

interface ReportViewProps {
  report: FinalReport;
  /** Only needed for the PDF/transcript downloads; hosts without them
      (the public share page) can omit it. */
  sessionId?: string;
  /** Real completion timestamp from the session; anchors the doc head. */
  completedAt?: string | null;
  /** Session start — only pass when it is the interview's own start (not an
      invite's creation time); duration is derived from the pair. */
  createdAt?: string | null;
  /** The session transcript; hosts that have it pass it so the dossier can
      show the conversation. Without it the evidence column shows a note. */
  transcript?: TranscriptEntryRead[] | null;
  /** Client-collected integrity signals (proctored invites). Rendered as a
      deterministic flags block — never merged into the AI's scores. */
  integrityEvents?: IntegrityEvent[] | null;
  /** Whether the interview's invite opted into proctoring. When true with no
      flagged events, the block still renders a clean "no flags" state —
      otherwise a proctored-but-clean report would be indistinguishable from
      an unproctored one. */
  proctored?: boolean | null;
  /** plan.extracted_skills — surfaced as chips when the host has the plan. */
  skills?: string[];
  /** The invite title, when the host knows it (the invites world). */
  roleTitle?: string;
  /** The report title's heading level; embedding hosts pass "h2" and keep
      their own page h1. */
  headingLevel?: "h1" | "h2";
  /** Workspace mode: fill the host viewport, pin the doc head, scroll each
      column in its own pane instead of scrolling the page. */
  fill?: boolean;
  /** Host-supplied actions rendered in the margin under the pinned rail head
      (the recording player on the candidate, admin and recruiter surfaces). */
  extraActions?: React.ReactNode;
  /** False hides the PDF/transcript download buttons — the public share page
      serves the document read-only, with no artifact endpoints behind it. */
  showDownloads?: boolean;
}

const isInterviewer = (speaker: string) =>
  /interviewer|aura|agent|assistant/i.test(speaker);

/**
 * The margin renders one subsection at a time under the pinned verdict (a
 * rail subnav swaps the panel) — the full stack made the rail a long
 * second scroll beside the conversation. Grades stays the default so the
 * scores are what you land on.
 */
type RailSection = "grades" | "signals" | "integrity" | "details";
const RAIL_SECTIONS: { id: RailSection; label: string }[] = [
  { id: "grades", label: "Grades" },
  { id: "signals", label: "Signals" },
  { id: "integrity", label: "Integrity" },
  { id: "details", label: "Details" },
];

const INTEGRITY_LABELS: Record<IntegrityEventType, string> = {
  tab_hidden: "left the tab",
  tab_visible: "returned to the tab",
  window_blur: "window lost focus",
  window_focus: "window refocused",
  paste: "pasted text",
  // Trust signals v2 — worker/evaluator-detected hints, not browser facts.
  long_pause_then_fluent: "long pause, then a fluent answer",
  answer_repeat: "repeated an earlier answer",
  speaking_rate_shift: "speaking rate jumped from their baseline",
  scripted_answer: "answer read as scripted",
};
// Recovery events (returned/refocused) mirror the flag events one-for-one,
// so only the signals themselves are counted and listed. V2 hints join the
// same deterministic summary, after the browser-recorded facts.
const INTEGRITY_FLAG_ORDER: IntegrityEventType[] = [
  "tab_hidden",
  "window_blur",
  "paste",
  "long_pause_then_fluent",
  "answer_repeat",
  "speaking_rate_shift",
  "scripted_answer",
];
const INTEGRITY_TIMELINE_CAP = 20;

/** mm:ss transcript timestamp, matching the host pages' transcript views. */
function stamp(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

/**
 * Deterministic integrity summary for proctored interviews: counts plus a
 * compact relative-time timeline. Deliberately not an LLM field and not a
 * score — industry practice (and the legal-safe posture) is to hand the
 * recruiter raw signals for their own judgment.
 */
function IntegrityBlock({ events, proctored }: { events: IntegrityEvent[]; proctored: boolean }) {
  const flagged = events.filter((e) => e.type !== "tab_visible" && e.type !== "window_focus");
  if (flagged.length === 0) {
    // Only a known-proctored session earns a clean block; without the flag,
    // "no events" is indistinguishable from "was never monitored".
    if (!proctored) return null;
    return (
      <div className="report-integrity">
        <h2 className="report-margin-head">Integrity signals</h2>
        <p className="report-integrity-summary">No flags — no tab switches, focus loss, or paste attempts.</p>
        <p className="report-integrity-note">
          Proctoring was on for this interview. Context for your review, not a score.
        </p>
      </div>
    );
  }
  const counts = flagged.reduce<Partial<Record<IntegrityEventType, number>>>((acc, e) => {
    acc[e.type] = (acc[e.type] ?? 0) + 1;
    return acc;
  }, {});
  const summary = INTEGRITY_FLAG_ORDER
    .filter((t) => counts[t])
    .map((t) => `${counts[t]}× ${INTEGRITY_LABELS[t]}`)
    .join(" · ");
  // Oldest events overflow off the top of the list — they stay in the
  // backend blob, the timeline just shows the most recent window.
  const shown = flagged.slice(-INTEGRITY_TIMELINE_CAP);
  const hidden = flagged.length - shown.length;

  return (
    <div className="report-integrity">
      <h2 className="report-margin-head">Integrity signals</h2>
      <p className="report-integrity-summary">{summary}</p>
      <ul className="report-integrity-timeline">
        {shown.map((e, i) => (
          <li key={`${e.type}-${e.at_ms}-${i}`}>
            <span className="report-stamp">{stamp(e.at_ms / 1000)}</span>
            <span>{INTEGRITY_LABELS[e.type]}</span>
          </li>
        ))}
      </ul>
      {hidden > 0 && <p className="report-integrity-more">+{hidden} earlier signals not shown</p>}
      <p className="report-integrity-note">
        Tab, focus, and paste signals are recorded by the browser; the remaining hints are
        detected by Aura's voice analysis. Context for your review, not a score.
      </p>
    </div>
  );
}

function recTone(rec: FinalReport["recommendation"]): "success" | "warn" | "danger" {
  if (rec === "Hold") return "warn";
  if (rec === "No Hire") return "danger";
  return "success";
}

/**
 * The interview report document, as a dossier: the verdict and dimension
 * notes in a margin, the conversation as the evidence column beside it.
 * Page chrome (back links, tabs, sidebar) belongs to the host page.
 */
export function ReportView({
  report,
  sessionId,
  completedAt,
  createdAt,
  transcript,
  integrityEvents,
  proctored,
  skills,
  roleTitle,
  headingLevel = "h1",
  fill = false,
  extraActions,
  showDownloads = true,
}: ReportViewProps) {
  const HeadingTag = headingLevel;
  const [downloading, setDownloading] = useState<'pdf' | 'transcript' | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [railSection, setRailSection] = useState<RailSection>("grades");
  // The verdict beam is a decorative accent; reduced motion gets a still card.
  const reduceMotion = useReducedMotion();

  const date = completedAt ? formatDate(completedAt) : null;
  const durationMin =
    createdAt && completedAt
      ? Math.max(1, Math.round((new Date(completedAt).getTime() - new Date(createdAt).getTime()) / 60000))
      : null;
  // Every interviewer line becomes an exchange; candidate lines attach as the
  // answer. The worker persists one entry per final STT event, so one spoken
  // answer can arrive as consecutive Candidate entries — a pause-split answer,
  // remarks after the wrap-up — and those absorb into the open answer's text
  // instead of being dropped. A candidate line before any interviewer line
  // (speech picked up while joining) stands alone in its own card.
  // Consecutive interviewer lines (greeting + first question, a follow-up
  // run-on) each keep their own exchange, and a trailing interviewer line —
  // the wrap-up — renders as an unanswered question. Nothing the transcript
  // holds is dropped from the evidence column.
  const exchanges: { q: string; qAt: number; answer?: { text: string; at: number } }[] = [];
  const entries = transcript ?? [];
  let current: (typeof exchanges)[number] | null = null;
  for (const entry of entries) {
    if (isInterviewer(entry.speaker)) {
      current = { q: entry.text, qAt: entry.timestamp_s };
      exchanges.push(current);
    } else if (current) {
      current.answer = current.answer
        ? { text: `${current.answer.text} ${entry.text}`, at: current.answer.at }
        : { text: entry.text, at: entry.timestamp_s };
    } else {
      current = { q: '', qAt: entry.timestamp_s, answer: { text: entry.text, at: entry.timestamp_s } };
      exchanges.push(current);
    }
  }
  // Only lines that actually precede an answer count as asked questions —
  // the greeting and the wrap-up must not inflate the head's "N questions",
  // and an unanchored candidate card has no question at all.
  const questions = exchanges.filter((x) => x.q && x.answer).length;

  const tone = recTone(report.recommendation);
  // The Integrity subsection (and its nav item) exists only when there is
  // something to show — the same rule IntegrityBlock uses to render nothing.
  const hasIntegrity =
    proctored === true ||
    (integrityEvents ?? []).some((e) => e.type !== "tab_visible" && e.type !== "window_focus");
  const subParts = [
    report.candidate_name,
    'Voice',
    durationMin ? `${durationMin} min` : null,
    questions > 0 ? (questions === 1 ? '1 question' : `${questions} questions`) : null,
  ].filter(Boolean);

  // Downloads go through the backend artifacts (GET /download/{id}/{type}):
  // the PDF is the server-rendered report document, shared with what gets
  // archived to MinIO.
  async function handleDownload(fileType: 'pdf' | 'transcript') {
    if (!sessionId) return
    setDownloadError(null);
    setDownloading(fileType);
    try {
      await downloadArtifact(sessionId, fileType, report.candidate_name);
    } catch {
      setDownloadError(`Could not download the ${fileType === 'pdf' ? 'PDF report' : 'transcript'}. Please try again.`);
    } finally {
      setDownloading(null);
    }
  }

  return (
    <div className={fill ? "aura-report-page is-fill" : "aura-report-page"}>
      {downloadError && (
        <div className="report-error" role="alert">{downloadError}</div>
      )}

      <header className="report-doc">
        <div className="report-id">
          <span className="report-eyebrow">Interview dossier{date ? ` · ${date}` : ''}</span>
          <HeadingTag className="report-title">{roleTitle ?? "Voice interview"}</HeadingTag>
          <p className="report-sub">{subParts.join(' · ')}</p>
          {skills && skills.length > 0 && (
            <ul className="report-skills" aria-label="Skills discussed">
              {/* Deduped: the parser can repeat a skill, and a duplicate
                  would also collide on the string key. */}
              {Array.from(new Set(skills)).map((sk) => <li key={sk}>{sk}</li>)}
            </ul>
          )}
        </div>
        {showDownloads && (
          <div className="report-doc-actions">
            <button className="btn btn-ghost" type="button" onClick={() => handleDownload('pdf')} disabled={downloading !== null}>
              {downloading === 'pdf' ? "Preparing…" : "Download PDF"}
            </button>
            <button className="btn btn-ghost" type="button" onClick={() => handleDownload('transcript')} disabled={downloading !== null}>
              {downloading === 'transcript' ? "Preparing…" : "Transcript"}
            </button>
          </div>
        )}
      </header>

      <div className="report-grid">
        <aside className="report-margin">
          {/* The verdict + subnav pin as one head in fill mode: a lone sticky
              verdict slid the subnav underneath itself, stranding the pills
              once the rail scrolled. The beam wrapper carries the beam
              (border-beam needs its own element to trace); the inner card
              keeps the verdict styling. */}
          <div className="report-rail-head">
            <BorderBeam size="md" colorVariant="ocean" theme="dark" active={!reduceMotion} borderRadius={12} className="report-verdict-beam">
              <div className="report-verdict">
                <span className="report-verdict-label">Verdict</span>
                {/* The single most important number in the product rolls in like
                    the dashboard stats do, instead of appearing as plain text
                    beside the animated note fills. */}
                <span className="report-verdict-score" aria-label={`Overall score ${report.overall_score} out of 100`}>
                  <NumberTicker value={report.overall_score} startOnView={false} /><span className="report-verdict-denom">/100</span>
                </span>
                <span className={`report-verdict-tag report-verdict-tag-${tone}`}>{report.recommendation}</span>
              </div>
            </BorderBeam>

            <div className="report-subnav" aria-label="Report sections">
              {RAIL_SECTIONS.filter(s => s.id !== "integrity" || hasIntegrity).map(s => (
                <button
                  key={s.id}
                  type="button"
                  aria-pressed={railSection === s.id}
                  onClick={() => setRailSection(s.id)}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          {/* Outside the keyed panel: the recording player must survive the
              subnav swap — remounting it would restart playback mid-listen. */}
          {extraActions && <div className="report-extra-actions">{extraActions}</div>}

          {/* Keyed by section: the swap replays the rise instead of teleporting. */}
          <div className="report-rail-panel" key={railSection}>
            {railSection === "grades" && (
              <>
                {report.section_grades.length >= 3 && (
                  <Suspense fallback={<div className="report-radar" />}>
                    <SectionGradesRadar grades={report.section_grades} />
                  </Suspense>
                )}

                <div className="report-notes">
                  <h2 className="report-margin-head">Dimension notes</h2>
                  {report.section_grades.map((g, i) => {
                    const pct = Math.max(0, Math.min(100, g.score * 10));
                    return (
                      <article key={i} className="report-note">
                        <div className="report-note-line">
                          <span className="report-note-name">{g.section_name}</span>
                          <span className="report-note-score">{g.score}<span className="report-note-denom">/10</span></span>
                        </div>
                        <span className="report-note-track" aria-hidden="true"><i className="report-note-fill" style={{ "--w": `${pct}%` } as CSSProperties}></i></span>
                        <p className="report-note-comment">{g.comments}</p>
                      </article>
                    );
                  })}
                </div>
              </>
            )}

            {railSection === "signals" && (
              <div className="report-lists">
                <h2 className="report-margin-head">Strengths</h2>
                <ul className="report-marginalia report-marginalia-s">
                  {report.strengths.map((t, i) => <li key={i}>{t}</li>)}
                </ul>
                <h2 className="report-margin-head">Watch-outs</h2>
                <ul className="report-marginalia report-marginalia-w">
                  {report.weaknesses.map((t, i) => <li key={i}>{t}</li>)}
                </ul>
              </div>
            )}

            {railSection === "integrity" && (
              <IntegrityBlock events={integrityEvents ?? []} proctored={proctored === true} />
            )}

            {railSection === "details" && (
              <p className="report-provenance">Generated by Aura{date ? ` · ${date}` : ''}</p>
            )}
          </div>
        </aside>

        <div className="report-main">
          <h2 className="report-conv-head">The conversation</h2>
          <p className="report-conv-sub">Exactly what was said, in order — the evidence behind the margin notes.</p>
          {exchanges.length > 0 ? (
            <ol className="report-exchanges">
              {exchanges.map((x, i) => (
                <li key={i} className="report-exchange" style={{ "--i": i } as CSSProperties}>
                  {x.q && (
                    <div className="report-q">
                      <span className="report-stamp">{stamp(x.qAt)}</span>
                      <p className="report-q-text">{x.q}</p>
                    </div>
                  )}
                  {x.answer && (
                    <div className="report-a">
                      <span className="report-stamp report-stamp-you">Candidate · {stamp(x.answer.at)}</span>
                      <p className="report-a-text">{x.answer.text}</p>
                    </div>
                  )}
                </li>
              ))}
            </ol>
          ) : (
            <p className="report-empty" style={{ marginTop: 18 }}>
              Transcript not available in this view.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
